/***
*
*   SIGNUP PROMOTIONAL DATA
*   View and analyze signup promotional data and company information
*
**********/

import { useState, useEffect } from 'react';
import {
  Animate,
  Card,
  Grid,
  Stat,
  Table,
  Loader,
  Paginate,
  Chart,
  useSignupPromotionalData
} from 'components/lib';
import Style from 'components/dashboard/dashboard.tailwind.js';

const chartStyle = {
  distributionGrid: 'grid grid-cols-1 md:grid-cols-2 gap-6 mb-6',
  featureList: 'space-y-2',
  featureItem: 'flex justify-between items-center p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors',
  featureName: 'text-sm font-medium text-slate-700',
  featureCount: 'text-sm font-bold text-brand-500',
  filterContainer: 'bg-white rounded-lg border border-slate-200 p-4 mb-6',
  filterRow: 'flex flex-wrap gap-4',
  filterGroup: 'flex-1 min-w-[200px]',
  searchInput: 'w-full p-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500',
  filterSelect: 'w-full p-3 border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-500',
};

// Distinct color palette for charts
const CHART_COLORS = [
  '#8B5CF6', // Purple
  '#10B981', // Green
  '#3B82F6', // Blue
  '#F59E0B', // Orange
  '#EF4444', // Red
  '#EC4899', // Pink
  '#14B8A6', // Teal
  '#6366F1', // Indigo
  '#84CC16', // Lime
  '#F97316', // Deep Orange
  '#06B6D4', // Cyan
  '#8B4513', // Saddle Brown
  '#9333EA', // Violet
  '#059669', // Emerald
  '#DC2626', // Rose Red
];

export function SignupPromotionalData(props) {
  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    company_size: '',
    company_type: '',
    search: ''
  });

  const [searchInput, setSearchInput] = useState('');

  const { data, loading, error } = useSignupPromotionalData(filters);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput, page: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (newOffset) => {
    const newPage = Math.floor(newOffset / filters.limit) + 1;
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  if (loading) return <Loader />;

  if (error) {
    return (
      <Card>
        <div className="text-center py-8">
          <p className="text-red-500">Error loading signup data</p>
          <p className="text-sm text-slate-400 mt-2">{error}</p>
        </div>
      </Card>
    );
  }

  const summary = data?.summary || {};
  const promotionalInfo = data?.promotional_info || {};
  const analytics = promotionalInfo?.analytics || {};
  const items = promotionalInfo?.items || [];
  const pagination = promotionalInfo?.pagination || {};

  // Prepare chart data for Company Size
  const companySizeEntries = Object.entries(analytics?.company_size_distribution || {});
  const companySizeData = {
    labels: companySizeEntries.map(([label]) => label),
    datasets: [{
      label: 'Company Size',
      data: companySizeEntries.map(([, value]) => value),
      backgroundColor: companySizeEntries.map((_, idx) => CHART_COLORS[idx % CHART_COLORS.length]),
      borderWidth: 2,
      borderColor: '#ffffff',
    }]
  };

  // Prepare chart data for Company Type
  const companyTypeEntries = Object.entries(analytics?.company_type_distribution || {});
  const companyTypeData = {
    labels: companyTypeEntries.map(([label]) => label),
    datasets: [{
      label: 'Company Type',
      data: companyTypeEntries.map(([, value]) => value),
      backgroundColor: companyTypeEntries.map((_, idx) => CHART_COLORS[idx % CHART_COLORS.length]),
      borderWidth: 2,
      borderColor: '#ffffff',
    }]
  };

  const topFeatures = (analytics?.feature_popularity || []).slice(0, 10);

  // Prepare table data
  const tableData = items.map(item => ({
    id: item._id,
    company_name: item.company_name,
    admin_name: item.admin_name,
    admin_email: item.admin_email,
    workspace_name: item.workspace_name,
    company_size: item.company_size,
    company_type: item.type_of_company,
    features_count: item.feature_liked?.length || 0,
    used_hrm: item.used_hrm || 'None',
    date_created: new Date(item.date_created).toLocaleDateString(),
    plan: item.workspace_plan
  }));

  return (
    <Animate type="pop">
      {/* Header */}
      <div className={Style.pageHeader}>
        <div>
          <h1 className={Style.pageTitle}>Signup Promotional Data</h1>
          <p className={Style.pageSubtitle}>
            Analyze company information and feature preferences from signups
          </p>
        </div>
      </div>

      {/* Summary Stats */}
      <Grid cols="4">
        <Stat
          value={summary.total_signups || 0}
          label="Total Signups"
          icon="users"
        />
        <Stat
          value={summary.total_feedback || 0}
          label="Feedback Received"
          icon="message-circle"
        />
        <Stat
          value={summary.avg_rating?.toFixed(1) || '0.0'}
          label="Avg Rating"
          icon="star"
        />
        <Stat
          value={topFeatures[0]?.feature?.replace(/_/g, ' ') || 'N/A'}
          label="Top Feature"
          icon="trending-up"
        />
      </Grid>

      {/* Filters */}
      <div className={chartStyle.filterContainer}>
        <div className={chartStyle.filterRow}>
          <div className={chartStyle.filterGroup}>
            <input
              type="text"
              placeholder="Search by company, admin, or workspace name..."
              className={chartStyle.searchInput}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          <div className={chartStyle.filterGroup}>
            <select
              className={chartStyle.filterSelect}
              value={filters.company_size}
              onChange={(e) => handleFilterChange('company_size', e.target.value)}
            >
              <option value="">All Company Sizes</option>
              {Object.keys(analytics?.company_size_distribution || {}).map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>

          <div className={chartStyle.filterGroup}>
            <select
              className={chartStyle.filterSelect}
              value={filters.company_type}
              onChange={(e) => handleFilterChange('company_type', e.target.value)}
            >
              <option value="">All Company Types</option>
              {Object.keys(analytics?.company_type_distribution || {}).map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Distribution Charts */}
      <div className={chartStyle.distributionGrid}>
        <Card title="Company Size Distribution">
          {companySizeEntries.length > 0 ? (
            <Chart
              type="donut"
              data={companySizeData}
              legend={true}
            />
          ) : (
            <p className="text-slate-500 text-center py-8">No data available</p>
          )}
        </Card>

        <Card title="Company Type Distribution">
          {companyTypeEntries.length > 0 ? (
            <Chart
              type="pie"
              data={companyTypeData}
              legend={true}
            />
          ) : (
            <p className="text-slate-500 text-center py-8">No data available</p>
          )}
        </Card>
      </div>

      {/* Feature Popularity */}
      <Grid cols="2">
        <Card title="Top 10 Popular Features">
          {topFeatures.length > 0 ? (
            <div className={chartStyle.featureList}>
              {topFeatures.map((feature, index) => (
                <div key={index} className={chartStyle.featureItem}>
                  <span className={chartStyle.featureName}>
                    {index + 1}. {feature.feature.replace(/_/g, ' ').toUpperCase()}
                  </span>
                  <span className={chartStyle.featureCount}>{feature.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No feature data available</p>
          )}
        </Card>

        <Card title="HRM Systems Used">
          {analytics?.used_hrm_distribution ? (
            <div className={chartStyle.featureList}>
              {Object.entries(analytics.used_hrm_distribution)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 10)
                .map(([hrm, count], index) => (
                  <div key={index} className={chartStyle.featureItem}>
                    <span className={chartStyle.featureName}>
                      {hrm === 'null' ? 'Not Specified' : hrm}
                    </span>
                    <span className={chartStyle.featureCount}>{count}</span>
                  </div>
                ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No HRM data available</p>
          )}
        </Card>
      </Grid>

      {/* Feature Bar Chart */}
      <Card title="Feature Popularity Chart">
        {topFeatures.length > 0 ? (
          <Chart
            type="bar"
            color="purple"
            data={{
              labels: topFeatures.map(f => f.feature.replace(/_/g, ' ').toUpperCase()),
              datasets: [{
                label: 'Popularity',
                data: topFeatures.map(f => f.count)
              }]
            }}
          />
        ) : (
          <p className="text-slate-500 text-center py-8">No data available</p>
        )}
      </Card>

      {/* Company Data Table */}
      <Card title="Company Details">
        <Table
          data={tableData}
          loading={loading}
          show={[
            'company_name',
            'admin_name',
            'admin_email',
            'company_size',
            'company_type',
            'features_count',
            'used_hrm',
            'date_created'
          ]}
          actions={{
            email: true
          }}
        />
      </Card>

      {/* Pagination */}
      {pagination.totalCount > filters.limit && (
        <Paginate
          offset={(filters.page - 1) * filters.limit}
          limit={filters.limit}
          total={pagination.totalCount}
          onChange={handlePageChange}
        />
      )}
    </Animate>
  );
}